/**
 * Comprobante de Partidas: un asiento por bloque, cargos frente a abonos.
 *
 * No es el Libro Diario con otra pintura. El libro es cronológico y se archiva
 * como libro; el comprobante respalda cada partida y se archiva por asiento. La
 * jerarquía técnica de Odoo —`account.group`— SÍ es aquí la estructura del
 * documento, al contrario que en un estado financiero, donde la manda el
 * mapping aprobado por el contador.
 *
 * Tres columnas por bloque y el nivel decide cuál lleva la cifra: el importe
 * del apunte va a «Parciales» y el total de su grupo a «Cargos» o «Abonos».
 * Nunca las dos, o el importe se leería dos veces.
 */

import { useState } from "react";
import { api } from "../api/client";
import { useAsync } from "../api/useAsync";
import type { JournalVoucher, VoucherSide } from "../api/types";
import { defaultPeriod } from "../components/period";
import type { Period } from "../components/period";
import { ExportMenu } from "../components/ExportMenu";
import { PeriodPicker } from "../components/PeriodPicker";
import { ErrorState, Loading } from "../components/States";
import { StatusBadge } from "../components/StatusBadge";
import { formatMoney, moneyClass } from "../components/format";
import { formatDateTime } from "../components/format";

/**
 * Un bloque, relleno hasta `filas` renglones.
 *
 * Los dos bloques SIEMPRE tienen el mismo número de filas, y no es cosmética.
 * Van uno al lado del otro en una rejilla que los estira a la misma altura, así
 * que el que tenía menos apuntes repartía el sobrante entre sus filas: los
 * abonos salían con renglones del doble de alto que los cargos, y la banda
 * cebra de los dos lados dejaba de coincidir. Igualando el CONTEO desaparece el
 * sobrante que había que repartir.
 *
 * Y de paso el documento gana lo que un comprobante necesita: cargos y abonos
 * enfrentados renglón a renglón, que es como se comparan.
 */
function Bloque({ lado, filas }: { lado: VoucherSide; filas: number }) {
  const relleno = Math.max(0, filas - lado.rows.length);
  return (
    <table className="datos comprobante">
      <thead>
        <tr>
          <th scope="col">Cuenta</th>
          <th scope="col">Concepto</th>
          <th scope="col" className="num">
            Parciales
          </th>
          <th scope="col" className="num">
            {lado.label}
          </th>
        </tr>
      </thead>
      <tbody>
        {lado.rows.map((fila, i) => (
          <tr key={`${fila.kind}-${fila.code}-${i}`} className={`nivel-${fila.kind}`}>
            <td className="mono">{fila.kind === "line" ? fila.code : ""}</td>
            <td style={{ paddingLeft: `${8 + fila.level * 14}px` }}>{fila.label}</td>
            {/* El apunte a «Parciales», el grupo hoja a «Cargos»/«Abonos». */}
            <td className={moneyClass(fila.partial)}>
              {fila.partial === null ? "" : formatMoney(fila.partial)}
            </td>
            <td className={moneyClass(fila.total)}>
              {fila.total === null ? "" : formatMoney(fila.total)}
            </td>
          </tr>
        ))}
        {Array.from({ length: relleno }, (_, i) => (
          <tr key={`relleno-${i}`} className="relleno" aria-hidden="true">
            <td colSpan={4} />
          </tr>
        ))}
      </tbody>
      <tfoot>
        <tr className="fila-total">
          <td colSpan={3}>Total {lado.label.toLowerCase()}</td>
          <td className={moneyClass(lado.total)}>{formatMoney(lado.total)}</td>
        </tr>
      </tfoot>
    </table>
  );
}

export function JournalVoucherPage() {
  const [period, setPeriod] = useState<Period>(defaultPeriod);
  const { data, error, loading } = useAsync(
    () => api.journalVouchers(period),
    [period.periodFrom, period.periodTo],
  );


  return (
    <>
      <div className="page-head">
        <div>
          <h1>Comprobante de Partidas</h1>
          <p>
            Un asiento por comprobante, con los cargos y los abonos agrupados por
            grupo de cuentas y las sumas iguales al pie. Los mismos apuntes del
            Libro Diario, en la forma en que se archivan.
          </p>
        </div>
      </div>

      <PeriodPicker value={period} onChange={setPeriod}>
        <ExportMenu
          documento={{
            label: "Comprobante de Partidas",
            note: "Un asiento por página, con el pie de firmas.",
            xlsx: api.downloadUrl("journal_voucher", "xlsx", period),
            pdf: api.downloadUrl("journal_voucher", "pdf", period),
          }}
        />
      </PeriodPicker>

      {loading && !data && <Loading what="los comprobantes" />}
      {error && !data && <ErrorState error={error} />}

      {data && data.length === 0 && (
        <div className="state">
          <p>Sin asientos en el período seleccionado.</p>
        </div>
      )}

      {data?.map((v: JournalVoucher) => (
          <section className="tabla comprobante-caja" key={v.move_name}>
            <div className="comprobante-cabeza">
              <div>
                <strong className="mono">{v.move_name}</strong>
                {v.move_ref && <span className="muted"> · {v.move_ref}</span>}
              </div>
              <div className="muted small">
                {formatDateTime(v.accounting_date)} · {v.journal_name}
              </div>
              {/* Cargos = Abonos, dicho EN el documento y no solo en un
                  validador: si no cuadra, al asiento le faltan apuntes. */}
              <StatusBadge tone={v.balanced ? "good" : "critical"}>
                {v.balanced ? "Cuadra" : "No cuadra"}
              </StatusBadge>
            </div>
            <div className="comprobante-lados">
              <Bloque
                lado={v.debit}
                filas={Math.max(v.debit.rows.length, v.credit.rows.length)}
              />
              <Bloque
                lado={v.credit}
                filas={Math.max(v.debit.rows.length, v.credit.rows.length)}
              />
            </div>
          </section>
      ))}

      <p className="small muted">
        La vista muestra los comprobantes del período; la descarga trae el
        documento completo con su procedencia y el pie de firmas.
      </p>
    </>
  );
}
